import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Masthead from "@/components/alcott/Masthead";
import AlcottFooter from "@/components/alcott/AlcottFooter";
import HorizonWaitlist from "@/components/alcott/HorizonWaitlist";
import HeroCanvas from "@/components/alcott/HeroCanvas";
import Seo from "@/components/Seo";
import { ALCOTT, LEAD, STORIES, ARCHIVES, IMG, SECRETS_IMG } from "@/data/alcott";

const ease = [0.22, 1, 0.36, 1];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-10%" }}
    transition={{ duration: 0.8, delay, ease }}
    className={className}
  >
    {children}
  </motion.div>
);

const StoryInner = ({ s }) => (
  <>
    <div className="relative aspect-[4/5] overflow-hidden rounded-sm">
      <img
        src={s.image}
        alt={s.title}
        className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
      />
    </div>
    <p className="mt-5 font-body text-[0.62rem] uppercase tracking-[0.22em] text-[#C05C42]">{s.category}</p>
    <h3 className="mt-2 font-serif text-2xl font-semibold leading-tight tracking-tight text-[#0A0A0A]">
      {s.title}
    </h3>
    <p className="mt-2 font-body text-sm leading-relaxed text-[#0A0A0A]/60">{s.excerpt}</p>
    <p className="mt-3 font-body text-[0.62rem] uppercase tracking-[0.18em] text-[#0A0A0A]/40">
      {s.author} · {s.readTime}
    </p>
  </>
);

export const AlcottHome = () => {
  const [waitlist, setWaitlist] = useState(false);

  return (
    <div className="min-h-screen bg-[#FAF9F6] font-body text-[#0A0A0A]" data-testid="alcott-home">
      <Seo
        title="A Cultural & Esoteric Review"
        description="Sybils & Society — a luxury review of heritage, fashion, art, and the sacred. Editorial essays by Veronica Bey celebrating Indigenous and Black women."
        image={LEAD.image}
      />
      <Masthead />

      {/* Cinematic 3D hero */}
      <section className="relative min-h-[94vh] w-full overflow-hidden bg-[#0A0A0A] text-[#FAF9F6]" data-testid="alcott-hero">
        <HeroCanvas />
        {/* grain */}
        <div
          className="pointer-events-none absolute inset-0 z-[1] opacity-[0.18] mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          }}
        />
        {/* radial vignette for depth */}
        <div className="pointer-events-none absolute inset-0 z-[1] bg-[radial-gradient(circle_at_center,transparent_35%,#0A0A0A_88%)]" />

        <div className="relative z-10 mx-auto grid min-h-[94vh] max-w-[1500px] grid-cols-1 items-center gap-12 px-5 py-32 md:px-10 lg:grid-cols-[1.08fr_0.92fr]">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease }}
          >
            <p className="font-body text-[0.62rem] uppercase tracking-[0.4em] text-[#C5A059]/80" data-testid="hero-issue">
              {ALCOTT.issue}
            </p>
            <p className="mt-6 font-body text-[0.7rem] uppercase tracking-[0.3em] text-[#C05C42]">{LEAD.category}</p>
            <h1 className="mt-5 font-serif text-[clamp(2.9rem,7.5vw,6rem)] font-light leading-[0.92] tracking-tight text-[#FAF9F6]" data-testid="hero-title">
              {LEAD.title}
            </h1>
            <p className="mt-7 max-w-xl font-body text-base leading-relaxed text-[#FAF9F6]/60 md:text-lg">{LEAD.dek}</p>
            <div className="mt-8 font-body text-[0.7rem] uppercase tracking-[0.24em] text-[#FAF9F6]/45">
              By {LEAD.author} · {LEAD.readTime}
            </div>
            <Link
              to={`/essay/${LEAD.slug}`}
              className="group relative mt-10 inline-flex items-center gap-2 pb-1.5 font-body text-[0.7rem] uppercase tracking-[0.24em] text-[#C5A059] after:absolute after:bottom-0 after:left-0 after:h-px after:w-full after:origin-bottom-right after:scale-x-100 after:bg-[#C5A059]/60 after:transition-transform after:duration-500 hover:after:origin-bottom-left"
              data-testid="hero-read-btn"
            >
              Read the essay
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>

            <div className="mt-6 flex flex-col items-start gap-3">
              {[
                { label: "Society Archives", to: "/archives", testid: "hero-link-archives" },
                { label: "Sybils Secrets", to: "/secrets", testid: "hero-link-secrets" },
                { label: "Sacred Scrolls", to: "/secrets#sacred-scrolls", testid: "hero-link-scrolls" },
                { label: "See & Do", to: "/experiences", testid: "hero-link-seedo" },
              ].map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="group relative inline-flex items-center gap-1.5 pb-1 font-body text-[0.7rem] uppercase tracking-[0.24em] text-[#C5A059]/85 transition-colors duration-300 hover:text-[#C5A059] after:absolute after:bottom-0 after:left-0 after:h-px after:w-full after:origin-bottom-right after:scale-x-0 after:bg-[#C5A059]/60 after:transition-transform after:duration-500 hover:after:origin-bottom-left hover:after:scale-x-100"
                  data-testid={l.testid}
                >
                  {l.label}
                  <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              ))}
            </div>
          </motion.div>

          <motion.div
            className="relative hidden justify-self-end lg:block"
            initial={{ opacity: 0, y: 50, rotateY: 8 }}
            animate={{ opacity: 1, y: 0, rotateY: 0 }}
            transition={{ duration: 1.2, delay: 0.2, ease }}
          >
            <div className="relative aspect-[4/5] w-[24rem] overflow-hidden rounded-xl border border-[#C5A059]/25 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)] backdrop-blur-sm xl:w-[27rem]">
              <img src={LEAD.image} alt={LEAD.title} className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/50 via-transparent to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-5">
                <p className="font-body text-[0.55rem] uppercase tracking-[0.28em] text-[#C5A059]">Contributor</p>
                <p className="mt-1 font-serif text-lg text-[#FAF9F6]">{LEAD.author}</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* scroll cue */}
        <motion.div
          className="pointer-events-none absolute inset-x-0 bottom-32 z-10 flex justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 0.4] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
        >
          <span className="font-body text-[0.55rem] uppercase tracking-[0.4em] text-[#FAF9F6]/40">Scroll</span>
        </motion.div>

        {/* dark → light transition into the editorial */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[2] h-48 bg-gradient-to-b from-transparent to-[#FAF9F6]" />
      </section>

      {/* Departments / featured grid */}
      <section className="border-t border-[#0A0A0A]/10 bg-[#FAF9F6] px-5 py-16 md:px-10 md:py-24" data-testid="alcott-featured">
        <div className="mx-auto max-w-[1500px]">
          <div className="mb-12 flex items-end justify-between">
            <h2 className="font-serif text-4xl font-semibold tracking-tight text-[#0A0A0A] md:text-5xl">
              From the current issue
            </h2>
            <Link to="/archives" className="hidden font-body text-[0.7rem] uppercase tracking-[0.2em] text-[#C05C42] hover:text-[#0A0A0A] md:inline">
              All stories →
            </Link>
          </div>

          <div className="grid grid-cols-1 gap-x-6 gap-y-14 sm:grid-cols-2 lg:grid-cols-5">
            {STORIES.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.06}>
                {s.internal ? (
                  <Link to={`/essay/${s.slug}`} className="group block" data-testid={`story-${s.id}`}>
                    <StoryInner s={s} />
                  </Link>
                ) : (
                  <a href={s.url} target="_blank" rel="noreferrer" className="group block" data-testid={`story-${s.id}`}>
                    <StoryInner s={s} />
                  </a>
                )}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Archives teaser */}
      <section className="bg-[#0A0A0A] px-5 py-20 md:px-10 md:py-28" data-testid="alcott-archives-teaser">
        <div className="mx-auto grid max-w-[1500px] grid-cols-1 items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <p className="font-body text-[0.7rem] uppercase tracking-[0.28em] text-[#C05C42]">The Archive</p>
            <h2 className="mt-5 font-serif text-[clamp(2.2rem,5vw,4rem)] font-semibold leading-[1] tracking-tight text-[#F4EFE6]">
              A century of letters, ledgers, and unsigned portraits.
            </h2>
            <p className="mt-6 max-w-lg font-body text-base leading-relaxed text-[#F4EFE6]/60">
              Our archive holds recovered correspondence, salon records, and cultural
              ephemera — annotated by the editors. Selected pieces are open; the deep
              archive lives within Sybils Society.
            </p>
            <div className="mt-9 flex flex-col items-start gap-3">
              <Link to="/archives" className="group relative inline-flex items-center gap-1.5 pb-1 font-body text-[0.7rem] uppercase tracking-[0.24em] text-[#C5A059]/85 transition-colors duration-300 hover:text-[#C5A059] after:absolute after:bottom-0 after:left-0 after:h-px after:w-full after:origin-bottom-right after:scale-x-0 after:bg-[#C5A059]/60 after:transition-transform after:duration-500 hover:after:origin-bottom-left hover:after:scale-x-100" data-testid="archives-teaser-btn">
                Enter the archives
                <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link to="/secrets#sacred-scrolls" className="group relative inline-flex items-center gap-1.5 pb-1 font-body text-[0.7rem] uppercase tracking-[0.24em] text-[#C5A059]/85 transition-colors duration-300 hover:text-[#C5A059] after:absolute after:bottom-0 after:left-0 after:h-px after:w-full after:origin-bottom-right after:scale-x-0 after:bg-[#C5A059]/60 after:transition-transform after:duration-500 hover:after:origin-bottom-left hover:after:scale-x-100" data-testid="archives-scrolls-btn">
                Sacred Scrolls
                <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link to="/archives#art-cities" className="group relative inline-flex items-center gap-1.5 pb-1 font-body text-[0.7rem] uppercase tracking-[0.24em] text-[#C5A059]/85 transition-colors duration-300 hover:text-[#C5A059] after:absolute after:bottom-0 after:left-0 after:h-px after:w-full after:origin-bottom-right after:scale-x-0 after:bg-[#C5A059]/60 after:transition-transform after:duration-500 hover:after:origin-bottom-left hover:after:scale-x-100" data-testid="archives-art-cities-btn">
                Art & Cities
                <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link to="/archives#fashion" className="group relative inline-flex items-center gap-1.5 pb-1 font-body text-[0.7rem] uppercase tracking-[0.24em] text-[#C5A059]/85 transition-colors duration-300 hover:text-[#C5A059] after:absolute after:bottom-0 after:left-0 after:h-px after:w-full after:origin-bottom-right after:scale-x-0 after:bg-[#C5A059]/60 after:transition-transform after:duration-500 hover:after:origin-bottom-left hover:after:scale-x-100" data-testid="archives-fashion-btn">
                Heritage & Fashion
                <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link to="/veil" onClick={() => sessionStorage.removeItem("sybil_entered")} className="group relative inline-flex items-center gap-1.5 pb-1 font-body text-[0.7rem] uppercase tracking-[0.24em] text-[#C5A059]/85 transition-colors duration-300 hover:text-[#C5A059] after:absolute after:bottom-0 after:left-0 after:h-px after:w-full after:origin-bottom-right after:scale-x-0 after:bg-[#C5A059]/60 after:transition-transform after:duration-500 hover:after:origin-bottom-left hover:after:scale-x-100" data-testid="archives-secret-btn">
                Sybils Society
                <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <a href="https://7gentrust.org" target="_blank" rel="noreferrer" className="group relative inline-flex items-center gap-1.5 pb-1 font-body text-[0.7rem] uppercase tracking-[0.24em] text-[#C5A059]/85 transition-colors duration-300 hover:text-[#C5A059] after:absolute after:bottom-0 after:left-0 after:h-px after:w-full after:origin-bottom-right after:scale-x-0 after:bg-[#C5A059]/60 after:transition-transform after:duration-500 hover:after:origin-bottom-left hover:after:scale-x-100" data-testid="archives-7gentrust-btn">
                7 Generations Trust
                <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="grid grid-cols-2 gap-4">
              {ARCHIVES.slice(0, 2).map((a) => (
                <div key={a.id} className="relative aspect-[3/4] overflow-hidden rounded-sm">
                  <img src={a.image} alt={a.title} className="h-full w-full object-cover opacity-90" />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                    <p className="font-body text-[0.55rem] uppercase tracking-[0.2em] text-[#C05C42]">{a.era}</p>
                    <p className="mt-1 font-serif text-lg text-[#F4EFE6]">{a.title}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Horizon Circle bridge */}
      <section className="relative overflow-hidden bg-[#F4EFE6]" data-testid="alcott-horizon-bridge">
        <div className="mx-auto grid max-w-[1500px] grid-cols-1 lg:grid-cols-2">
          <div className="order-2 flex flex-col justify-center px-5 py-16 md:px-10 md:py-24 lg:order-1">
            <Reveal>
              <p className="font-body text-[0.7rem] uppercase tracking-[0.28em] text-[#C05C42]">The Horizon Circle</p>
              <h2 className="mt-5 font-serif text-[clamp(2.2rem,5vw,4rem)] font-semibold leading-[1] tracking-tight text-[#0A0A0A]">
                Where culture meets global mobility.
              </h2>
              <p className="mt-6 max-w-lg font-body text-base leading-relaxed text-[#0A0A0A]/65">
                Our travel and lifestyle society for the modern vanguard — private
                culinary salons, boutique heritage retreats, and international
                gatherings, across three tiers of membership.
              </p>
              <div className="mt-9 flex flex-wrap gap-4">
                <a href="https://thehorizoncircle.co" target="_blank" rel="noreferrer" className="group inline-flex items-center gap-2 rounded-full bg-[#0A0A0A] px-6 py-3 font-body text-[0.65rem] uppercase tracking-[0.2em] text-[#FAF9F6] transition-transform duration-300 hover:-translate-y-0.5" data-testid="horizon-bridge-btn">
                  Discover the Circle <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
                <Link to="/experiences" className="rounded-full border border-[#0A0A0A]/25 px-6 py-3 font-body text-[0.65rem] uppercase tracking-[0.2em] text-[#0A0A0A] transition-colors duration-300 hover:border-[#0A0A0A]" data-testid="horizon-bridge-experiences-btn">
                  View experiences
                </Link>
              </div>
            </Reveal>
          </div>
          <div className="order-1 h-[46vh] overflow-hidden lg:order-2 lg:h-auto">
            <img src={IMG.horizonFeature} alt="The Horizon Circle" className="h-full w-full object-cover" />
          </div>
        </div>
      </section>

      {/* Sybils Society entrance point */}
      <section className="relative overflow-hidden bg-[#0A0A0A] text-[#F4EFE6]" data-testid="alcott-sybils-bridge">
        {/* cosmic grain */}
        <div
          className="pointer-events-none absolute inset-0 z-[1] opacity-[0.14] mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          }}
        />
        <div className="relative z-10 mx-auto grid max-w-[1500px] grid-cols-1 lg:grid-cols-2">
          <div className="order-1 h-[46vh] overflow-hidden lg:h-auto">
            <img src={SECRETS_IMG.portrait1} alt="The Sybils Society" className="h-full w-full object-cover opacity-90" />
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent to-[#0A0A0A]/70 lg:w-1/2" />
          </div>
          <div className="order-2 flex flex-col justify-center px-5 py-16 md:px-10 md:py-24">
            <Reveal>
              <p className="font-body text-[0.7rem] uppercase tracking-[0.28em] text-[#C5A059]">The Sybils Society</p>
              <h2 className="mt-5 font-serif text-[clamp(2.2rem,5vw,4rem)] font-light leading-[1] tracking-tight text-[#F4EFE6]">
                You stand at the threshold.
              </h2>
              <p className="mt-6 max-w-lg font-body text-base leading-relaxed text-[#F4EFE6]/60">
                A clandestine order for modern matriarchs — a cosmic sanctum of daily
                oracles, minted sigils, and the deep archive. Entry is by covenant, across
                three tiers of the Matriarch line.
              </p>
              <div className="mt-9 flex flex-wrap gap-4">
                <Link
                  to="/veil"
                  onClick={() => sessionStorage.removeItem("sybil_entered")}
                  className="group inline-flex items-center gap-2 rounded-full bg-[#C5A059] px-6 py-3 font-body text-[0.65rem] uppercase tracking-[0.2em] text-[#0A0A0A] transition-transform duration-300 hover:-translate-y-0.5"
                  data-testid="sybils-bridge-veil-btn"
                >
                  Enter the threshold <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
                <Link
                  to="/secrets"
                  className="rounded-full border border-[#F4EFE6]/30 px-6 py-3 font-body text-[0.65rem] uppercase tracking-[0.2em] text-[#F4EFE6] transition-colors duration-300 hover:bg-[#F4EFE6] hover:text-[#0A0A0A]"
                  data-testid="sybils-bridge-oracle-btn"
                >
                  Consult the Oracle
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <AlcottFooter onJoin={() => setWaitlist(true)} />
      <HorizonWaitlist open={waitlist} onClose={() => setWaitlist(false)} variant="outer" />
    </div>
  );
};

export default AlcottHome;
